import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "../imap/logo.svg";
import {
  connectionString,
  hostComponent,
  IMAP_ACCESS_TOKEN_PLACEHOLDER,
  portComponent,
  tlsComponent,
} from "../imap/shared";

export const imapXoauth2 = defineConnector({
  identifier: "imapXoauth2",
  name: "IMAP (XOAUTH2)",
  tagLine: "Read email over IMAP with OAuth 2.0",
  logo,
  tags: ["communication"],
  protocol: "imap",
  connectionTest: { protocol: "imap", connectionString },
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: oauthAuth,
  oauth: {
    grantType: () => "authorization_code",
    clientCredentialsFrom: "db",
    authorizationEndpoint: (cfg) => cfg.authorizeUrl ?? "",
    tokenEndpoint: (cfg) => cfg.tokenUrl ?? "",
    tokenEndpointAuthMethod: () => "client_secret_post",
    pkce: (cfg) => cfg.pkce === "on",
    scopes: (cfg) => (cfg.scopes ?? "").split(/\s+/).filter(Boolean),
  },
  steps: [
    {
      title: "Enter your IMAP server",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "IMAP (XOAUTH2)",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "input",
          label: "Host",
          required: true,
          placeholder: "imap.gmail.com",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Port",
          required: true,
          defaultValue: "993",
          target: { kind: "configuration", field: "port" },
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          placeholder: "you@example.com",
          helperText: "The mailbox address to authenticate as.",
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "select",
          label: "TLS",
          required: true,
          defaultValue: "implicit",
          target: { kind: "configuration", field: "tls" },
          options: [
            { value: "implicit", label: "Implicit TLS (IMAPS, port 993)" },
            { value: "starttls", label: "STARTTLS (port 143)" },
            { value: "off", label: "Disabled" },
          ],
        },
        {
          kind: "file",
          label: "CA certificate",
          accept: ".pem,.crt,.cer",
          helperText:
            "Upload a PEM CA certificate for private or self-signed IMAP servers.",
          target: { kind: "configuration", field: "tlsCaCertificate" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "tls" },
              notValue: "off",
            },
          ],
        },
      ],
    },
    {
      title: "Connect with OAuth 2.0",
      items: [
        {
          kind: "markdown",
          content:
            "Register an OAuth 2.0 application with your mail provider and copy the redirect URI below into its allowed callbacks. For Gmail use the authorization URL `https://accounts.google.com/o/oauth2/v2/auth`, token URL `https://oauth2.googleapis.com/token`, and scope `https://mail.google.com/`.",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/imapXoauth2/callback",
        },
        {
          kind: "url",
          label: "Authorization URL",
          required: true,
          placeholder: "https://accounts.google.com/o/oauth2/v2/auth",
          target: { kind: "configuration", field: "authorizeUrl" },
        },
        {
          kind: "url",
          label: "Token URL",
          required: true,
          placeholder: "https://oauth2.googleapis.com/token",
          target: { kind: "configuration", field: "tokenUrl" },
        },
        {
          kind: "input",
          label: "Scopes",
          required: true,
          placeholder: "https://mail.google.com/",
          helperText: "Space-separated.",
          target: { kind: "configuration", field: "scopes" },
        },
        {
          kind: "select",
          label: "PKCE",
          target: { kind: "configuration", field: "pkce" },
          defaultValue: "off",
          options: [
            { value: "off", label: "Disabled" },
            { value: "on", label: "Enabled" },
          ],
          helperText: "Enable if your provider requires it.",
        },
        {
          kind: "input",
          label: "Client ID",
          required: true,
          placeholder: "Paste your client ID",
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          placeholder: "Paste your client secret",
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "configuration", field: "authorizeUrl" },
            { kind: "configuration", field: "tokenUrl" },
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  publicEnvVars: ({ host, port, username, tls }) => ({
    IMAP_HOST: hostComponent(host),
    IMAP_PORT: portComponent(port),
    IMAP_USERNAME: username ?? "",
    IMAP_ACCESS_TOKEN: IMAP_ACCESS_TOKEN_PLACEHOLDER,
    IMAP_TLS: tlsComponent(tls),
  }),
  usageHint: () =>
    "Read IMAP_HOST, IMAP_PORT, IMAP_USERNAME, and IMAP_ACCESS_TOKEN from the environment to connect with SASL XOAUTH2. IMAP_TLS is one of 'implicit', 'starttls', or 'off'.",
});
